/**
 * Binary stroke wire-frame codec (M2.T15).
 *
 * Per IMPLEMENTATION_PLAN §5.2, §13.2.d, binary frames travel over the
 * WebSocket connection after a successful HELLO/WELCOME JSON handshake.
 * The first byte of every binary frame is the frame-type discriminant.
 *
 * Frame layout (all multi-byte integers are little-endian):
 *
 *   STROKE_START  (type=0x01)
 *     [u8 frameType, u8 brushSize, u8 colorIdx, u32 strokeIdNonce]  → 7 bytes total
 *
 *   STROKE_BATCH  (type=0x02)
 *     [u8 frameType, u32 strokeIdNonce, u32 seq, samples...]  → 9 + N*13 bytes
 *     Each sample: [f32 x, f32 y, f32 pressure, u8 pointerType]  → 13 bytes
 *
 *   STROKE_END    (type=0x03)
 *     [u8 frameType, u32 strokeIdNonce, u32 pathByteLen, path-utf8-bytes]
 *     → 9 + pathByteLen bytes
 *
 * colorIdx ∈ [0..7] maps to the 8-brand palette (§13.2.d, expanded in M2 polish).
 * colorIdx = 8 is the eraser sentinel.
 * brushSize ∈ {0, 1, 2} for thin/medium/thick.
 * pointerType ∈ {0=mouse, 1=touch, 2=pen}.
 *
 * All encode/decode functions are pure (no IO). The server uses these in
 * RoomRuntime.acceptStrokeBatch and the STROKE_END router (M2.T16+).
 */

import type { BrandColor } from "./types.js";

// ---------------------------------------------------------------------------
// Frame type discriminants
// ---------------------------------------------------------------------------

export const FRAME_TYPE_STROKE_START = 0x01 as const;
export const FRAME_TYPE_STROKE_BATCH = 0x02 as const;
export const FRAME_TYPE_STROKE_END = 0x03 as const;

export type StrokeFrameType =
  | typeof FRAME_TYPE_STROKE_START
  | typeof FRAME_TYPE_STROKE_BATCH
  | typeof FRAME_TYPE_STROKE_END;

// ---------------------------------------------------------------------------
// Color palette: index ↔ BrandColor mapping (§13.2.d)
// ---------------------------------------------------------------------------

/**
 * The 8-color brand palette in index order (expanded from 6 in M2 polish).
 * colorIdx ∈ [0..7] maps to a BrandColor; colorIdx = 8 is the eraser sentinel.
 */
export const PALETTE_COLORS: ReadonlyArray<BrandColor> = [
  "#000000", // 0 = ink black
  "#e63946", // 1 = red
  "#ffd23f", // 2 = yellow
  "#4361ee", // 3 = blue
  "#7cb518", // 4 = green
  "#8b5a2b", // 5 = brown
  "#f77f00", // 6 = orange (M2 polish: user feedback)
  "#9d4edd", // 7 = purple (M2 polish: user feedback)
];

export const ERASER_COLOR_IDX = 8 as const;

/** Decode colorIdx → { color, isEraser }. */
export function decodeColorIdx(colorIdx: number): { color: BrandColor; isEraser: boolean } {
  if (colorIdx === ERASER_COLOR_IDX) {
    return { color: "#000000", isEraser: true };
  }
  const color = PALETTE_COLORS[colorIdx];
  if (color === undefined) {
    throw new RangeError(`Invalid colorIdx: ${colorIdx}`);
  }
  return { color, isEraser: false };
}

/** Encode BrandColor + isEraser flag → colorIdx. */
export function encodeColorIdx(color: BrandColor, isEraser: boolean): number {
  if (isEraser) return ERASER_COLOR_IDX;
  const idx = PALETTE_COLORS.indexOf(color);
  if (idx === -1) throw new RangeError(`Unknown BrandColor: ${color}`);
  return idx;
}

// ---------------------------------------------------------------------------
// PointerType encoding
// ---------------------------------------------------------------------------

export const POINTER_TYPE_MOUSE = 0 as const;
export const POINTER_TYPE_TOUCH = 1 as const;
export const POINTER_TYPE_PEN = 2 as const;

export type PointerTypeCode =
  | typeof POINTER_TYPE_MOUSE
  | typeof POINTER_TYPE_TOUCH
  | typeof POINTER_TYPE_PEN;

export function encodePointerType(pointerType: string): PointerTypeCode {
  switch (pointerType) {
    case "touch":
      return POINTER_TYPE_TOUCH;
    case "pen":
      return POINTER_TYPE_PEN;
    default:
      return POINTER_TYPE_MOUSE;
  }
}

export function decodePointerType(code: number): "mouse" | "touch" | "pen" {
  switch (code) {
    case POINTER_TYPE_TOUCH:
      return "touch";
    case POINTER_TYPE_PEN:
      return "pen";
    default:
      return "mouse";
  }
}

// ---------------------------------------------------------------------------
// Parsed frame types
// ---------------------------------------------------------------------------

export type ParsedStrokeStart = {
  frameType: typeof FRAME_TYPE_STROKE_START;
  brushSize: 0 | 1 | 2;
  colorIdx: number;
  strokeIdNonce: number;
};

/** A single decoded sample: logical x/y in [0,1600]×[0,1200], pressure [0,1]. */
export type DecodedSample = {
  x: number;
  y: number;
  pressure: number;
  pointerType: "mouse" | "touch" | "pen";
};

export type ParsedStrokeBatch = {
  frameType: typeof FRAME_TYPE_STROKE_BATCH;
  strokeIdNonce: number;
  seq: number;
  samples: ReadonlyArray<DecodedSample>;
};

export type ParsedStrokeEnd = {
  frameType: typeof FRAME_TYPE_STROKE_END;
  strokeIdNonce: number;
  path: string;
};

export type ParsedStrokeFrame = ParsedStrokeStart | ParsedStrokeBatch | ParsedStrokeEnd;

// ---------------------------------------------------------------------------
// STROKE_START codec
// ---------------------------------------------------------------------------

/** Encode a STROKE_START frame. Returns a 7-byte ArrayBuffer. */
export function encodeStrokeStart(
  brushSize: 0 | 1 | 2,
  colorIdx: number,
  strokeIdNonce: number,
): ArrayBuffer {
  const buf = new ArrayBuffer(7);
  const view = new DataView(buf);
  view.setUint8(0, FRAME_TYPE_STROKE_START);
  view.setUint8(1, brushSize);
  view.setUint8(2, colorIdx);
  view.setUint32(3, strokeIdNonce, /* littleEndian */ true);
  return buf;
}

/** Decode a STROKE_START frame. Throws on malformed input. */
export function decodeStrokeStart(buf: ArrayBuffer): ParsedStrokeStart {
  if (buf.byteLength < 7) {
    throw new RangeError(`STROKE_START too short: ${buf.byteLength} bytes`);
  }
  const view = new DataView(buf);
  const brushSize = view.getUint8(1);
  if (brushSize !== 0 && brushSize !== 1 && brushSize !== 2) {
    throw new RangeError(`Invalid brushSize: ${brushSize}`);
  }
  const colorIdx = view.getUint8(2);
  // colorIdx ∈ [0..7] = brand palette; 8 = eraser sentinel. Reject anything >8.
  if (colorIdx > ERASER_COLOR_IDX) {
    throw new RangeError(`Invalid colorIdx: ${colorIdx} (valid range 0–${ERASER_COLOR_IDX})`);
  }
  return {
    frameType: FRAME_TYPE_STROKE_START,
    brushSize: brushSize as 0 | 1 | 2,
    colorIdx,
    strokeIdNonce: view.getUint32(3, true),
  };
}

// ---------------------------------------------------------------------------
// STROKE_BATCH codec
// ---------------------------------------------------------------------------

export const BYTES_PER_SAMPLE = 13 as const;

/** Encode a STROKE_BATCH frame. */
export function encodeStrokeBatch(
  strokeIdNonce: number,
  seq: number,
  samples: ReadonlyArray<DecodedSample>,
): ArrayBuffer {
  const headerSize = 9; // 1 (frameType) + 4 (nonce) + 4 (seq)
  const buf = new ArrayBuffer(headerSize + samples.length * BYTES_PER_SAMPLE);
  const view = new DataView(buf);
  view.setUint8(0, FRAME_TYPE_STROKE_BATCH);
  view.setUint32(1, strokeIdNonce, true);
  view.setUint32(5, seq, true);
  let offset = headerSize;
  for (const s of samples) {
    view.setFloat32(offset, s.x, true);
    view.setFloat32(offset + 4, s.y, true);
    view.setFloat32(offset + 8, s.pressure, true);
    view.setUint8(offset + 12, encodePointerType(s.pointerType));
    offset += BYTES_PER_SAMPLE;
  }
  return buf;
}

/** Decode a STROKE_BATCH frame. */
export function decodeStrokeBatch(buf: ArrayBuffer): ParsedStrokeBatch {
  const headerSize = 9;
  if (buf.byteLength < headerSize) {
    throw new RangeError(`STROKE_BATCH too short: ${buf.byteLength} bytes`);
  }
  const view = new DataView(buf);
  const strokeIdNonce = view.getUint32(1, true);
  const seq = view.getUint32(5, true);
  const sampleBytes = buf.byteLength - headerSize;
  if (sampleBytes % BYTES_PER_SAMPLE !== 0) {
    throw new RangeError(`STROKE_BATCH sample bytes not divisible by ${BYTES_PER_SAMPLE}`);
  }
  const sampleCount = sampleBytes / BYTES_PER_SAMPLE;
  const samples: DecodedSample[] = [];
  let offset = headerSize;
  for (let i = 0; i < sampleCount; i++) {
    samples.push({
      x: view.getFloat32(offset, true),
      y: view.getFloat32(offset + 4, true),
      pressure: view.getFloat32(offset + 8, true),
      pointerType: decodePointerType(view.getUint8(offset + 12)),
    });
    offset += BYTES_PER_SAMPLE;
  }
  return { frameType: FRAME_TYPE_STROKE_BATCH, strokeIdNonce, seq, samples };
}

// ---------------------------------------------------------------------------
// STROKE_END codec
// ---------------------------------------------------------------------------

/** Encode a STROKE_END frame. */
export function encodeStrokeEnd(strokeIdNonce: number, path: string): ArrayBuffer {
  const pathBytes = new TextEncoder().encode(path);
  const buf = new ArrayBuffer(9 + pathBytes.length);
  const view = new DataView(buf);
  view.setUint8(0, FRAME_TYPE_STROKE_END);
  view.setUint32(1, strokeIdNonce, true);
  view.setUint32(5, pathBytes.length, true);
  new Uint8Array(buf).set(pathBytes, 9);
  return buf;
}

/** Decode a STROKE_END frame. */
export function decodeStrokeEnd(buf: ArrayBuffer): ParsedStrokeEnd {
  if (buf.byteLength < 9) {
    throw new RangeError(`STROKE_END too short: ${buf.byteLength} bytes`);
  }
  const view = new DataView(buf);
  const strokeIdNonce = view.getUint32(1, true);
  const pathByteLen = view.getUint32(5, true);
  if (buf.byteLength < 9 + pathByteLen) {
    throw new RangeError(
      `STROKE_END truncated: claimed ${pathByteLen} path bytes but only ${buf.byteLength - 9} available`,
    );
  }
  const pathBytes = new Uint8Array(buf, 9, pathByteLen);
  const path = new TextDecoder().decode(pathBytes);
  return { frameType: FRAME_TYPE_STROKE_END, strokeIdNonce, path };
}

// ---------------------------------------------------------------------------
// Top-level dispatcher
// ---------------------------------------------------------------------------

/**
 * Parse any binary stroke frame from a raw ArrayBuffer.
 * Throws `RangeError` on malformed input or unknown frame type.
 */
export function parseStrokeFrame(buf: ArrayBuffer): ParsedStrokeFrame {
  if (buf.byteLength === 0) {
    throw new RangeError("Empty binary frame");
  }
  const frameType = new DataView(buf).getUint8(0);
  switch (frameType) {
    case FRAME_TYPE_STROKE_START:
      return decodeStrokeStart(buf);
    case FRAME_TYPE_STROKE_BATCH:
      return decodeStrokeBatch(buf);
    case FRAME_TYPE_STROKE_END:
      return decodeStrokeEnd(buf);
    default:
      throw new RangeError(`Unknown binary frame type: 0x${frameType.toString(16)}`);
  }
}
